import { type IconName, icons, type SvgComponent } from "@inflow/icons"
import { motion } from "framer-motion"
import React, { Suspense, useEffect, useMemo } from "react"

import { cn } from "@/utilities/etc"
import { log } from "@/utilities/logging"

import styles from "./Icon.module.css"

interface Props {
  name: IconName
  size?: "small" | "default" | "large" | "x-large"
  className?: React.HTMLAttributes<HTMLDivElement>["className"]
  // Left and right adds a margin to icon container. Solo is for targetting in other class, ex. buttons
  slot?: "left" | "right" | "solo"
  style?: React.CSSProperties
  retainOriginalColors?: boolean
}

// Class of icon is added for easier targeting of the element in other css modules

const Icon = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const Element = useMemo(() => {
    const lookup = icons as Record<string, SvgComponent | undefined>
    return lookup[props.name]
  }, [props.name])

  useEffect(() => {
    if (Element) return
    log({
      level: "warn",
      scope: "misconfiguration",
      flow: "misc",
      message: `Icon "${props.name}" could not be resolved from @inflow/icons`,
      meta: { name: props.name },
    })
  }, [Element, props.name])

  return (
    <div
      ref={ref}
      className={cn("icon", styles.icon, props.className)}
      data-size={props.size || "default"}
      data-slot={props.slot || undefined}
      data-retain-original-colors={props.retainOriginalColors || undefined}
      style={props.style}
    >
      {Element && (
        <Suspense fallback={null}>
          <Element />
        </Suspense>
      )}
    </div>
  )
})

export const MotionIcon = motion.create(Icon)

export default Icon
